import React, { useEffect, useRef, useState } from "react";
import { FiSearch } from "react-icons/fi";
import clsx from "clsx";
import { Button } from "./Button";
import { Calendar, type CalendarLabels } from "./Calendar";
import { getAvailabilityLoadingState } from "../utils/availabilityLoadingState";
import { GuestSelect, type GuestSelectProps } from "./GuestSelect";
import type { BookingAvailability } from "../../../api-contracts/sites/wbe/booking-availability";
import type { LanguageCode } from "../api/contracts/language-settings";
import { applyActivityRangeSelect, applyRangeSelect } from "../utils/dateRangeUtils";

export interface BookingSearchLabels {
  arrival: string;
  departure: string;
  guests: string;
  selectDate: string;
  defaultGuests: string;
  formatDate: (date: Date | null) => string;
  formatGuests: () => string;
}

export interface BookingSearchProps {
  /**
   * Chrome variant. "app" uses Tailwind utility classes; "embed" uses BEM
   * class names matched by embed.css so the shell renders correctly inside
   * the widget's shadow DOM.
   */
  variant?: "app" | "embed";

  selectedStartDate: Date | null;
  selectedEndDate: Date | null;
  currentMonth: Date;
  onMonthChange: (date: Date) => void;
  onDatesChange: (from: Date | null, to: Date | null) => void;
  bookingAvailability?: BookingAvailability;

  /**
   * True while the availability call is in-flight. Composite renders a thin
   * progress indicator at the top of the calendar popover so the user knows
   * the dates may still be updating.
   */
  availabilityLoading?: boolean;
  /**
   * The feed in `bookingAvailability` answers a different window than the one
   * on screen. Callers backed by react-query's `keepPreviousData` get this from
   * `isPlaceholderData`; the embed widget tracks it by hand.
   */
  availabilityStale?: boolean;

  /**
   * GuestSelect props built by the caller. The composite renders
   * <GuestSelect {...guestSelectProps} /> inside the guests popover by
   * default; pass `renderGuestsPopover` to wrap it with app-specific UI
   * (multi-unit tabs, footer actions).
   */
  guestSelectProps: GuestSelectProps;

  calendarLabels: CalendarLabels;
  languageCode: LanguageCode;
  labels: BookingSearchLabels;

  /**
   * Activity-site date selection: the first click picks a complete single
   * day and a later click widens it. Also skips the auto-advance to the
   * guests pill — a completed selection is not the end of date editing.
   */
  singleDaySelect?: boolean;

  /** Parent-controlled disable (e.g. no dates picked, or stop-time blocked). */
  searchDisabled?: boolean;
  /** Called when the search icon button is pressed. */
  onSearch: () => void;
  /**
   * Called when popovers close (click-outside or after committing a date
   * range). Parents that need "auto-search on close" behavior hook this.
   */
  onPopoversClose?: () => void;
  /**
   * Called when popovers transition from closed to open (either pill clicked
   * while neither popover was active). Parents that stage edits in local
   * draft state use this as the "editing started" signal — it is the only
   * reliable source of truth, since the popover open/close state lives in
   * this composite's local `activePill`.
   */
  onPopoversOpen?: () => void;

  /**
   * Where to render the popovers vertically relative to the search bar.
   * Defaults to "bottom". Callers can pass "top" to flip — useful when
   * the search bar sits near the viewport bottom (e.g. an embedded widget
   * on a host page) and a bottom-placed popover would clip off-screen.
   */
  popoverPlacement?: "bottom" | "top";

  /**
   * Wrap the default <GuestSelect /> with app-specific UI. Receives the
   * default node and a `closePopovers` callback so an in-popover action
   * (e.g. an "Update" button) can dismiss the popover after committing.
   * Example: app wraps with multi-unit tabs + add/update buttons.
   */
  renderGuestsPopover?: (
    defaultGuestSelect: React.ReactNode,
    closePopovers: () => void,
  ) => React.ReactNode;

  /**
   * External request to open a specific pill's popover (e.g. NoResults'
   * "Ändra datum" button on landing). The composite's own `activePill` is
   * internal state, so we expose this as a one-shot trigger: set the prop
   * to a pill name, and `onOpenPillRequestHandled` fires once the popover
   * has been opened so the caller can clear the trigger back to `null`.
   */
  openPillRequest?: "dates" | "guests" | null;
  onOpenPillRequestHandled?: () => void;

  /** Future: extra pills to the left of the dates pill. */
  beforeDates?: React.ReactNode;
  /**
   * Extra content between the guests pill and the search button. The render
   * function form receives the shared pill-state context so a slot pill with
   * its own popover participates in the same active/otherActive styling and
   * mutual-exclusion as the dates/guests pills: report open/close via
   * `onOpenChange`, dim via `otherActive`, highlight via `open`.
   */
  afterGuests?: React.ReactNode | ((ctx: AfterGuestsContext) => React.ReactNode);
}

export type AfterGuestsContext = {
  /** The slot pill's popover is open (mirror this into the popover). */
  open: boolean;
  /** A built-in pill (dates/guests) popover is open — render dimmed. */
  otherActive: boolean;
  /** Report the slot popover's open/close intents here. */
  onOpenChange: (open: boolean) => void;
};

// --- variant-aware class strings ---------------------------------------

const shellClasses = (variant: "app" | "embed", popoversOpen: boolean) =>
  variant === "embed"
    ? clsx("search-bar", popoversOpen ? "active" : "inactive")
    : clsx(
        "flex items-center rounded-full border border-border py-0.5 pl-0.5 pr-1.5 transition-[background-color,box-shadow] duration-200 ease-out motion-reduce:transition-none",
        popoversOpen ? "bg-surface-hover shadow-inner" : "bg-surface-card shadow-card",
      );

const pillContainerClasses = (
  variant: "app" | "embed",
  state: "active" | "otherActive" | "inactive",
  wide: boolean,
) => {
  if (variant === "embed") {
    return clsx(
      "search-button-container",
      wide && "date-range-button-container",
      state === "active" && "active",
      state === "otherActive" && "other-active",
      state === "inactive" && "inactive",
    );
  }
  return clsx(
    wide ? "flex-[2]" : "flex-1",
    "rounded-full transition-[background-color,box-shadow] duration-200 ease-out motion-reduce:transition-none",
    state === "active"
      ? "bg-surface-card shadow-lg"
      : state === "otherActive"
        ? "bg-transparent"
        : "bg-transparent hover:bg-border",
  );
};

const pillButtonClasses = (variant: "app" | "embed") =>
  variant === "embed"
    ? "search-button"
    : "font-inter w-full !rounded-none !border-0 !p-0 text-left";

// --- pill subcomponents ------------------------------------------------

const Pill: React.FC<{
  variant: "app" | "embed";
  label: string;
  value: string;
  active: boolean;
  otherActive: boolean;
  onClick: () => void;
  ariaExpanded?: boolean;
  testId?: string;
}> = ({ variant, label, value, active, otherActive, onClick, ariaExpanded, testId }) => {
  const state = active ? "active" : otherActive ? "otherActive" : "inactive";
  return (
    <div className={pillContainerClasses(variant, state, false)}>
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        aria-expanded={ariaExpanded}
        className={pillButtonClasses(variant)}
      >
        {variant === "embed" ? (
          <div className="search-button-content">
            <span className="search-button-label">{label}</span>
            <span className="search-button-value">{value}</span>
          </div>
        ) : (
          <div className="flex flex-col items-start px-4 py-3">
            <span className="text-xs font-medium text-ink-secondary">
              {label}
            </span>
            <span className="text-xs font-bold">{value}</span>
          </div>
        )}
      </button>
    </div>
  );
};

const DateRangePill: React.FC<{
  variant: "app" | "embed";
  arrivalLabel: string;
  departureLabel: string;
  arrivalValue: string;
  departureValue: string;
  active: boolean;
  otherActive: boolean;
  onClick: () => void;
  ariaExpanded?: boolean;
}> = ({
  variant,
  arrivalLabel,
  departureLabel,
  arrivalValue,
  departureValue,
  active,
  otherActive,
  onClick,
  ariaExpanded,
}) => {
  const state = active ? "active" : otherActive ? "otherActive" : "inactive";
  return (
    <div className={pillContainerClasses(variant, state, true)}>
      <button
        type="button"
        data-testid="date-range-button"
        onClick={onClick}
        aria-expanded={ariaExpanded}
        className={pillButtonClasses(variant)}
      >
        {variant === "embed" ? (
          <>
            <div className="search-button-content">
              <span className="search-button-label">{arrivalLabel}</span>
              <span className="search-button-value">{arrivalValue}</span>
            </div>
            <div className="date-range-divider" />
            <div className="search-button-content">
              <span className="search-button-label">{departureLabel}</span>
              <span className="search-button-value">{departureValue}</span>
            </div>
          </>
        ) : (
          <div className="flex items-stretch">
            <div className="flex flex-1 flex-col items-start px-4 py-3">
              <span className="text-xs font-medium text-ink-secondary">
                {arrivalLabel}
              </span>
              <span className="text-xs font-bold">{arrivalValue}</span>
            </div>
            <div className="flex flex-1 flex-col items-start px-4 py-3">
              <span className="text-xs font-medium text-ink-secondary">
                {departureLabel}
              </span>
              <span className="text-xs font-bold">{departureValue}</span>
            </div>
          </div>
        )}
      </button>
    </div>
  );
};

// --- composite ---------------------------------------------------------

export const BookingSearch: React.FC<BookingSearchProps> = ({
  variant = "app",
  selectedStartDate,
  selectedEndDate,
  currentMonth,
  onMonthChange,
  onDatesChange,
  bookingAvailability,
  guestSelectProps,
  calendarLabels,
  languageCode,
  labels,
  singleDaySelect,
  searchDisabled,
  availabilityLoading,
  availabilityStale,
  onSearch,
  onPopoversClose,
  onPopoversOpen,
  popoverPlacement = "bottom",
  openPillRequest = null,
  onOpenPillRequestHandled,
  renderGuestsPopover,
  beforeDates,
  afterGuests,
}) => {
  const [activePill, setActivePill] = useState<"dates" | "guests" | null>(null);

  // With no feed at all the calendar renders every date as bookable, so the
  // first read has to be placeholder cells rather than an optimistic grid. A
  // refetch on top of a feed we already have keeps the grid and takes the bar.
  const availabilityLoadingState = getAvailabilityLoadingState({
    isFetching: !!availabilityLoading,
    isPlaceholderData: !!availabilityStale,
    hasData: !!bookingAvailability,
  });

  // Honour an external "open pill" request (e.g. NoResults' Ändra datum on
  // landing). The ref dedups on the trigger value so re-renders that pass
  // freshly-allocated callback lambdas don't reopen a popover the user just
  // dismissed — the effect only acts on an actual change of the request.
  const lastHandledOpenPillRequest = useRef<typeof openPillRequest>(null);
  useEffect(() => {
    if (!openPillRequest) {
      lastHandledOpenPillRequest.current = null;
      return;
    }
    if (lastHandledOpenPillRequest.current === openPillRequest) return;
    lastHandledOpenPillRequest.current = openPillRequest;
    setActivePill(openPillRequest);
    onPopoversOpen?.();
    onOpenPillRequestHandled?.();
  }, [openPillRequest, onPopoversOpen, onOpenPillRequestHandled]);
  const showCalendar = activePill === "dates";
  const showGuestSelect = activePill === "guests";
  const popoversOpen = activePill !== null;

  // Open state of a slot pill's popover (e.g. the booking-code segment).
  // Mutually exclusive with the built-in popovers: opening one side closes
  // the other, and either side keeps the shell in its "editing" look.
  const [slotPopoverOpen, setSlotPopoverOpen] = useState(false);
  const anyPopoverOpen = popoversOpen || slotPopoverOpen;
  const handleSlotOpenChange = (open: boolean) => {
    setSlotPopoverOpen(open);
    if (open && activePill !== null) {
      setActivePill(null);
      onPopoversClose?.();
    }
  };
  const afterGuestsNode =
    typeof afterGuests === "function"
      ? afterGuests({
          open: slotPopoverOpen,
          otherActive: popoversOpen,
          onOpenChange: handleSlotOpenChange,
        })
      : afterGuests;

  const searchBarRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const guestSelectRef = useRef<HTMLDivElement>(null);

  // Click-outside — uses composedPath so it works in both shadow DOM and
  // light DOM. Calls onPopoversClose so the parent can react (auto-search,
  // analytics, etc.) before the popovers close.
  useEffect(() => {
    if (!popoversOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const path: EventTarget[] =
        (event as unknown as { composedPath?: () => EventTarget[] }).composedPath?.() ??
        [event.target as EventTarget];

      const inside = (ref: React.RefObject<HTMLDivElement | null>) => {
        const target = ref.current;
        if (!target) return false;
        // composedPath() can include Window / ShadowRoot which aren't Node —
        // contains() throws on those. Guard with instanceof Node.
        return path.some(
          (node) =>
            node === target || (node instanceof Node && target.contains(node)),
        );
      };

      if (inside(searchBarRef) || inside(calendarRef) || inside(guestSelectRef)) {
        return;
      }
      onPopoversClose?.();
      setActivePill(null);
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popoversOpen, onPopoversClose]);

  const handleDatePillClick = () => {
    if (activePill === null) onPopoversOpen?.();
    setActivePill((prev) => (prev === "dates" ? null : "dates"));
  };
  const handleGuestsPillClick = () => {
    if (activePill === null) onPopoversOpen?.();
    setActivePill((prev) => (prev === "guests" ? null : "guests"));
  };

  const handleCalendarRangeChange = (from: Date, to: Date | null) => {
    onDatesChange(from, to);
    if (to && !singleDaySelect && window.innerWidth >= 768) {
      setActivePill("guests");
    }
  };

  // Single-tap fallback for callers that still pass an onDateSelect-style
  // flow (the legacy non-range API). Internally we always operate in range
  // mode via applyRangeSelect.
  const handleCalendarDateSelect = (date: Date) => {
    const { from, to } = singleDaySelect
      ? applyActivityRangeSelect(date, selectedStartDate, selectedEndDate)
      : applyRangeSelect(date, selectedStartDate, selectedEndDate);
    handleCalendarRangeChange(from, to);
  };

  const handleSearchClick = () => {
    if (searchDisabled) return;
    setActivePill(null);
    onSearch();
  };

  const popoverPositionClass =
    variant === "embed"
      ? clsx(
          variant === "embed" && "calendar-popover", // styled via embed.css
        )
      : "absolute left-1/2 z-50 -translate-x-1/2";
  const popoverInlineStyle =
    variant === "embed"
      ? popoverPlacement === "bottom"
        ? { top: "100%", bottom: "auto", marginTop: 8 }
        : { top: "auto", bottom: "100%", marginBottom: 8 }
      : popoverPlacement === "bottom"
        ? ({ top: "100%", marginTop: 8 } as const)
        : ({ bottom: "100%", marginBottom: 8 } as const);

  const guestPopoverPositionClass =
    variant === "embed"
      ? "guest-select-popover"
      : "absolute left-1/2 z-50 -translate-x-1/2";

  const defaultGuestSelect = <GuestSelect {...guestSelectProps} />;

  return (
    <div className={variant === "app" ? "relative w-[580px] max-w-full" : "relative"} data-testid="search-bar">
      <div ref={searchBarRef} className={shellClasses(variant, anyPopoverOpen)}>
        {variant === "embed" ? (
          <div className="search-buttons-group">
            {beforeDates}
            <DateRangePill
              variant={variant}
              arrivalLabel={labels.arrival}
              departureLabel={labels.departure}
              arrivalValue={labels.formatDate(selectedStartDate)}
              departureValue={labels.formatDate(selectedEndDate)}
              active={showCalendar}
              otherActive={anyPopoverOpen && !showCalendar}
              onClick={handleDatePillClick}
              ariaExpanded={showCalendar}
            />
            <Pill
              variant={variant}
              testId="guests-button"
              label={labels.guests}
              value={labels.formatGuests()}
              active={showGuestSelect}
              otherActive={anyPopoverOpen && !showGuestSelect}
              onClick={handleGuestsPillClick}
              ariaExpanded={showGuestSelect}
            />
            {afterGuestsNode}
          </div>
        ) : (
          <div className="flex flex-1 items-center">
            {beforeDates}
            <DateRangePill
              variant={variant}
              arrivalLabel={labels.arrival}
              departureLabel={labels.departure}
              arrivalValue={labels.formatDate(selectedStartDate)}
              departureValue={labels.formatDate(selectedEndDate)}
              active={showCalendar}
              otherActive={anyPopoverOpen && !showCalendar}
              onClick={handleDatePillClick}
              ariaExpanded={showCalendar}
            />
            <Pill
              variant={variant}
              testId="guests-button"
              label={labels.guests}
              value={labels.formatGuests()}
              active={showGuestSelect}
              otherActive={anyPopoverOpen && !showGuestSelect}
              onClick={handleGuestsPillClick}
              ariaExpanded={showGuestSelect}
            />
            {afterGuestsNode}
          </div>
        )}

        {variant === "embed" ? (
          <button
            type="button"
            onClick={handleSearchClick}
            disabled={!!searchDisabled}
            className={clsx("search-icon-button", searchDisabled && "disabled")}
            data-testid="search-button"
          >
            <FiSearch className="search-icon" />
          </button>
        ) : (
          <Button
            variant="icon"
            size="large"
            icon={<FiSearch className="h-6 w-6" />}
            disabled={!!searchDisabled}
            onClick={handleSearchClick}
            className={clsx(
              "transition-opacity duration-200",
              searchDisabled && "cursor-not-allowed opacity-50",
            )}
            data-testid="search-button"
          />
        )}
      </div>

      {showCalendar && (
        <div
          ref={calendarRef}
          className={popoverPositionClass}
          style={variant === "embed" ? popoverInlineStyle : popoverInlineStyle}
          data-testid="calendar-popover"
        >
          <Calendar
            labels={calendarLabels}
            languageCode={languageCode}
            selectedStartDate={selectedStartDate}
            selectedEndDate={selectedEndDate}
            bookingAvailability={bookingAvailability}
            isLoading={availabilityLoadingState.isStale}
            isRefreshing={availabilityLoadingState.isRefreshing}
            onDateSelect={handleCalendarDateSelect}
            currentMonth={currentMonth}
            onMonthChange={onMonthChange}
            variant="double"
            requireRange
            singleDaySelect={singleDaySelect}
            onRangeChange={handleCalendarRangeChange}
          />
        </div>
      )}

      {showGuestSelect && (
        <div
          ref={guestSelectRef}
          className={guestPopoverPositionClass}
          style={variant === "embed" ? popoverInlineStyle : popoverInlineStyle}
          data-testid="guest-select-popover"
        >
          {renderGuestsPopover
            ? renderGuestsPopover(defaultGuestSelect, () => setActivePill(null))
            : defaultGuestSelect}
        </div>
      )}
    </div>
  );
};
